import type { DeviceType } from '@renderer/types/utils'

/**
 * Which device type actually has a handle behind it.
 *
 * The camera store keeps the label (`deviceType`) and the handles (`webcam`, `camera`) as
 * separate fields, and the transitions between them are not atomic — the DSLR connect
 * disconnects and nulls the webcam before it starts, and writes the new label only at the
 * very end, after the USB/gphoto2 work. Any failure in between leaves the OLD label
 * standing over a handle that is gone.
 *
 * `capturePreview` then matches neither of its branches and throws on every frame for as
 * long as the page lives, while the settings modal still shows a green "Connected"
 * (it reads `webcam || camera`, which the surviving handle satisfies). Field, 2026-09-17.
 *
 * Several paths can produce that mismatch, so the repair belongs where the value is USED.
 * Returns `stored` whenever it is consistent, or when nothing is connected at all (the
 * ordinary "no device" case, which callers already handle).
 */
export function usableDeviceType(
  stored: DeviceType | undefined,
  hasWebcam: boolean,
  hasCamera: boolean
): DeviceType | undefined {
  const labelledHandleExists =
    stored === 'webcam' ? hasWebcam : stored === 'camera' ? hasCamera : false
  if (labelledHandleExists) return stored
  if (hasWebcam) return 'webcam'
  if (hasCamera) return 'camera'
  return stored
}
